import { Component, type ReactNode } from 'react';
import { Button } from './ui/button';
import { AlertCircle } from 'lucide-react';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
  componentStack?: string;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: { componentStack: string }) {
    console.error('[ErrorBoundary] Error:', error.message);
    console.error('[ErrorBoundary] Stack:', error.stack);
    console.error('[ErrorBoundary] Component stack:', info.componentStack);
    this.setState({ componentStack: info.componentStack });
  }

  reset = () => this.setState({ error: null });

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
        <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-lg">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
            <AlertCircle className="h-6 w-6 text-red-600" />
          </div>
          <h1 className="mt-4 text-center text-xl font-semibold text-gray-900">
            Something went wrong
          </h1>
          <p className="mt-2 text-center text-sm text-gray-600">
            We're sorry for the inconvenience. Our team has been notified and is working on a fix.
          </p>
          {import.meta.env.DEV && (
            <div className="mt-4 max-h-64 overflow-auto rounded border border-red-200 bg-red-50 p-3 text-left">
              <p className="mb-2 font-mono text-xs font-bold text-red-800">{error.message}</p>
              <pre className="whitespace-pre-wrap font-mono text-xs text-red-700">
                {error.stack}
              </pre>
              <pre className="mt-2 whitespace-pre-wrap font-mono text-xs text-orange-700">
                {(this.state as any).componentStack}
              </pre>
            </div>
          )}
          <div className="mt-6 flex flex-col gap-2">
            <Button onClick={this.reset} className="w-full">
              Try again
            </Button>
            <Button
              onClick={() => (window.location.href = '/')}
              variant="outline"
              className="w-full"
            >
              Go to homepage
            </Button>
          </div>
          <p className="mt-4 text-center text-xs text-gray-500">
            If this problem persists, please contact support
          </p>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
